<template>
  <v-row no-gutters class="slider-component">
    <v-col cols="12">
      <h2 class="section-heading f-24 sz-white--text font-weight-medium text-center" v-orphans>
        {{ title }}
      </h2>
    </v-col>
    <v-col cols="12">
      <v-row no-gutters>
        <v-col cols="12">
          <div class="d-flex">
            <v-sheet
              width="350"
              min-width="350"
              elevation="4"
              rounded
              color="white"
              class="sz-primary-grey--text font-weight-medium mx-3 d-none d-lg-block"
            >
              <v-img :src="QuotesSliderSrc" class="rounded" />
            </v-sheet>
            <v-slide-group
              ref="sliderRef"
              center-active
              :class="{
                'content-center': contentCenter,
                'slider-component__group--fade-left': fadeLeft,
                'slider-component__group--fade-right': fadeRight
              }"
            >
              <template #default>
                <v-slide-group-item v-for="(n, index) in items" :key="index" v-slot="{ toggle }">
                  <slot :id="index" :item="n" :toggle="toggle" />
                </v-slide-group-item>
              </template>
              <template #prev>
                <v-btn
                  :height="BUTTON_SIZE"
                  :width="BUTTON_SIZE"
                  color="white"
                  icon="$chevronLeft"
                  variant="flat"
                  :aria-label="t('label.previous')"
                />
              </template>
              <template #next>
                <v-btn
                  :height="BUTTON_SIZE"
                  :width="BUTTON_SIZE"
                  color="white"
                  icon="$chevronRight"
                  variant="flat"
                  :aria-label="t('label.next')"
                />
              </template>
            </v-slide-group>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import type { QuotesSlider } from '@/types'
import {
  getSliderCutEdges,
  getSliderPageTarget,
  type SliderCardBox,
  type SliderPagingDirection
} from '@/utils/sliderPaging'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { VSlideGroup } from 'vuetify/lib/components/index.mjs'
import QuotesSliderSrc from '@/assets/images/quotesSlider/1.jpg?format=webp&w=350&h=260'

type Props = {
  items: QuotesSlider[]
  title: string
  contentCenter: boolean
}

defineProps<Props>()

const { t } = useI18n()

const BUTTON_SIZE = 36

const sliderRef = ref<InstanceType<typeof VSlideGroup> | null>(null)

// With margins - the card should land with its margin, like in the initial position.
// Without margins - the visible card, used to detect a cut card.
const getCardBoxes = (
  container: HTMLElement,
  content: HTMLElement,
  withMargins = true
): SliderCardBox[] => {
  const containerLeft = container.getBoundingClientRect().left

  return Array.from(content.children).map((child) => {
    const rect = child.getBoundingClientRect()
    const style = getComputedStyle(child)
    const marginLeft = withMargins ? parseFloat(style.marginLeft) || 0 : 0
    const marginRight = withMargins ? parseFloat(style.marginRight) || 0 : 0

    return {
      left: rect.left - containerLeft + container.scrollLeft - marginLeft,
      right: rect.right - containerLeft + container.scrollLeft + marginRight
    }
  })
}

const getSliderElements = () => {
  const root = sliderRef.value?.$el as HTMLElement | undefined
  const container = root?.querySelector<HTMLElement>('.v-slide-group__container')
  const content = root?.querySelector<HTMLElement>('.v-slide-group__content')

  return container && content ? { container, content } : null
}

// The edge is faded only where a card is actually cut - a card that lands fully visible
// (e.g. after clicking the arrow) must not be faded, so its text stays readable
const fadeLeft = ref(false)
const fadeRight = ref(false)

const updateFade = (): void => {
  const elements = getSliderElements()
  if (!elements) return

  const { container, content } = elements
  const edges = getSliderCutEdges(
    getCardBoxes(container, content, false),
    container.scrollLeft,
    container.clientWidth
  )
  fadeLeft.value = edges.left
  fadeRight.value = edges.right
}

// Scrolls by cards, not by the container width (Vuetify default) - so a card is never left cut
// in half after clicking the arrow and the whole quote can be read
const scrollByPage = (direction: SliderPagingDirection): void => {
  const elements = getSliderElements()
  if (!elements) return

  const { container, content } = elements

  const left = getSliderPageTarget(
    direction,
    getCardBoxes(container, content),
    container.scrollLeft,
    container.clientWidth
  )
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  container.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' })
}

// The arrow area (Vuetify "affix") is the whole height of the slider, much bigger than the button.
// A click anywhere in it must be handled here - in the capture phase, before Vuetify scrolls
// by the full container width and leaves a card cut in half.
const onArrowClick = (event: MouseEvent): void => {
  const affix = (event.target as HTMLElement).closest<HTMLElement>(
    '.v-slide-group__prev, .v-slide-group__next'
  )
  if (!affix) return

  event.stopPropagation()

  const direction: SliderPagingDirection = affix.matches('.v-slide-group__prev') ? 'prev' : 'next'
  if (affix.matches(`.v-slide-group__${direction}--disabled`)) return

  scrollByPage(direction)
}

let resizeObserver: ResizeObserver | null = null
let observedContainer: HTMLElement | null = null
let observedRoot: HTMLElement | null = null

onMounted(() => {
  const elements = getSliderElements()
  if (!elements) return

  observedRoot = sliderRef.value?.$el as HTMLElement
  observedRoot.addEventListener('click', onArrowClick, true)
  observedContainer = elements.container
  observedContainer.addEventListener('scroll', updateFade, { passive: true })
  resizeObserver = new ResizeObserver(updateFade)
  resizeObserver.observe(elements.container)
  resizeObserver.observe(elements.content)
  updateFade()
})

onBeforeUnmount(() => {
  observedRoot?.removeEventListener('click', onArrowClick, true)
  observedContainer?.removeEventListener('scroll', updateFade)
  resizeObserver?.disconnect()
})
</script>

<style lang="scss">
// Gentle fade of a cut off card - only on the side where a card is actually cut (see `updateFade`).
// The card is dimmed at the edge, not hidden (mask instead of blur: the blur made the text hard to read)
.slider-component .v-slide-group {
  --slider-fade-size: 64px;
  --slider-fade-edge: rgb(0 0 0 / 80%);

  .v-slide-group__container {
    mask-image: linear-gradient(
      to right,
      var(--slider-fade-edge),
      #000 var(--slider-fade-left, 0),
      #000 calc(100% - var(--slider-fade-right, 0px)),
      var(--slider-fade-edge)
    );
  }

  &.slider-component__group--fade-left {
    --slider-fade-left: var(--slider-fade-size);
  }

  &.slider-component__group--fade-right {
    --slider-fade-right: var(--slider-fade-size);
  }
}
</style>
